import * as THREE from 'three';
import type { BackdropLayer as LayerSpec, EnvironmentDef } from '../config/environments';

const BASE = `${import.meta.env.BASE_URL}assets/images/`;

interface Layer {
  spec: LayerSpec;
  mesh: THREE.Mesh;
  texture: THREE.Texture;
  /** World width of one tile. */
  tileWidth: number;
}

/**
 * Painted parallax background. Each layer is a camera-anchored plane whose texture scrolls by a
 * fraction of the camera's travel and repeats seamlessly, so it never runs out.
 */
export class Backdrop {
  readonly group = new THREE.Group();
  private readonly layers: Layer[] = [];

  static async create(env: EnvironmentDef): Promise<Backdrop> {
    const loader = new THREE.TextureLoader();
    const layers = env.visual.backdrop;
    const textures = await Promise.all(layers.map((l) => loader.loadAsync(BASE + l.file)));
    return new Backdrop(layers, textures);
  }

  dispose(): void {
    for (const { mesh, texture } of this.layers) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      texture.dispose();
    }
    this.group.removeFromParent();
  }

  private constructor(layers: LayerSpec[], textures: THREE.Texture[]) {
    layers.forEach((spec, i) => {
      const texture = textures[i];
      texture.colorSpace = THREE.SRGBColorSpace;
      // The images are made seamless in art/images/process.py, so they repeat without mirroring.
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.offset.y = spec.vOffset;
      texture.anisotropy = 4;
      const image = texture.image as { width: number; height: number };
      const tileWidth = spec.tileHeight * (image.width / image.height);
      // Wide enough for any aspect at this depth (portrait zooms out, landscape is wide).
      const width = spec.height * 4;
      texture.repeat.set(width / tileWidth, spec.height / spec.tileHeight);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        color: spec.tint,
        transparent: spec.transparent,
        depthWrite: false,
        fog: false,
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, spec.height), material);
      mesh.position.z = spec.z;
      mesh.renderOrder = -10 + i;
      mesh.frustumCulled = false;
      this.group.add(mesh);
      this.layers.push({ spec, mesh, texture, tileWidth });
    });
  }

  update(cameraX: number, cameraY: number): void {
    for (const { spec, mesh, texture, tileWidth } of this.layers) {
      mesh.position.x = cameraX;
      mesh.position.y = cameraY * spec.followY + spec.offsetY;
      // Scroll so the image drifts by `parallax` of the camera's travel.
      texture.offset.x = spec.uOffset + (cameraX * spec.parallax) / tileWidth;
    }
  }
}
